import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DSSeparator } from '@elliemae/ds-separator';
import { isShallowEqual } from '@frontend/pricing-search';
import { DataProvider } from '../../../common/utils/customHooks/useAppData';
import {
  getClientSettingSelector,
  getRateSelectorAction,
  getSettingAction,
} from '../../../data/lockSummary/actionCreators';
import { closeLoader, openLoader } from '../../../data/screenLoader/actions';
import { AppDispatch } from '../../../store';
import {
  StyledEppsContainerBody,
  StyledEppsWrapper,
} from '../../global/styles/styled';
import { CommentsSection } from '../../LockSummary/components/CommentsSection';
import { GeneralLoanInformation } from '../../LockSummary/components/GeneralLoanInformation/GeneralLoanInformation';
import { InformationGroupSection } from '../../LockSummary/components/InformationGroupSection';
import { getTableDataWithIDs } from '../../LockSummary/components/PricingDetailsModal/helpers';
import { PricingTable } from '../../LockSummary/components/PricingTable';
import { SummaryHeader } from '../../LockSummary/components/SummaryHeader';
import { StatusFailMessage } from '../../LockSummary/components/SummaryHeader/StatusFailMessage';
import ScenarioFooter from '../../Scenario/components/ScenarioFooter';
import { exitToLegacyDebug } from '../utils';

// Read-only Tech Ops summary. Reuses the LockSummary sub-components but omits
// all write actions (no lock/float/save FooterActionButtons, no Create Loan),
// so there is no buyside/sellside access.
export const DebugSummary = () => {
  const [dataTableContext, setTableDataContext] = useState<any>({
    rawPrice: [],
    adjustedPrice: [],
  });
  const dispatch = useDispatch<AppDispatch>();
  const priceData = useSelector(
    ({
      epps: {
        lockSummary: { rateSelector },
      },
    }) => rateSelector.priceData,
    isShallowEqual,
  );

  useEffect(() => {
    if (priceData) setTableDataContext(getTableDataWithIDs(priceData));
  }, [priceData]);

  useEffect(() => {
    // getSettingAction closes the shared screen loader in its own finally, so run
    // it last rather than letting it race lockrates and hide the loader early.
    dispatch(openLoader('Loading'));
    Promise.all([
      dispatch(getRateSelectorAction()),
      dispatch(getClientSettingSelector()),
    ])
      .then(() => dispatch(getSettingAction()))
      .finally(() => dispatch(closeLoader()));
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <StatusFailMessage />
      <StyledEppsContainerBody>
        <StyledEppsWrapper>
          <DataProvider value={{ dataTableContext, setTableDataContext }}>
            <SummaryHeader actions="print" />
            <GeneralLoanInformation />
            <DSSeparator
              margin="s"
              type="non-form"
              orientation="horizontal"
              position="initial"
            />
            <PricingTable />
            <InformationGroupSection />
            <CommentsSection />
          </DataProvider>
        </StyledEppsWrapper>
      </StyledEppsContainerBody>
      <ScenarioFooter onCancel={exitToLegacyDebug} cancelLabel="Cancel" />
    </>
  );
};

export default DebugSummary;
